import React, { useEffect, useRef, useState } from "react";

import { faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { styled } from "styled-components";

import { Button } from ".";
import { ComponentColor, ComponentSize } from "./types";

export const Modal = ({
    children,
    className,
    title,
    size = ComponentSize.XS,
    okLabel,
    cancelLabel,
    okColor = ComponentColor.Primary,
    cancelColor = ComponentColor.Neutral,
    showAction,
    onClose,
    onOk,
}: ModalProps): React.JSX.Element => {
    const [isOk, setIsOk] = useState(false);
    const modalId = useRef<string | null>(null);
    const [currentModal, setCurrentModal] = useState<HTMLDialogElement | null>(
        null
    );

    useEffect(() => {
        if (modalId.current === null) {
            modalId.current = Date.now().toString();
            setCurrentModal(
                document.getElementById(modalId.current) as HTMLDialogElement
            );
        }
        currentModal?.showModal();
    }, [currentModal]);

    const closeModal = (): void => {
        currentModal?.close();
    };

    const handlerOnModalClose = (): void => {
        closeModal();

        onClose?.();
    };

    const handleOnModalOk = async (): Promise<void> => {
        if (onOk) {
            onOk();
        }

        closeModal();
    };

    const modalBoxClasses = classNames(
        "modal-box",
        "!p-0",
        "flex",
        "flex-col",
        size === ComponentSize.LG ? "!max-w-full" : "",
        size === ComponentSize.MD ? "!max-w-screen-md" : "",
        size === ComponentSize.SM ? "!max-w-screen-sm" : "",
        className
    );

    return (
        <ModalContainer
            id={currentModal ? currentModal.id : undefined}
            className="modal"
            onCancel={handlerOnModalClose}
        >
            <div className={modalBoxClasses}>
                <form method="dialog">
                    <div
                        className="absolute right-4 top-2 cursor-pointer"
                        onClick={handlerOnModalClose}
                    >
                        <FontAwesomeIcon icon={faX} />
                    </div>
                </form>
                <div className="modal-title font-bold text-lg p-4">{title}</div>
                <div className="modal-content px-4 pb-4 max-h-full overflow-auto">
                    {children}
                </div>
                {showAction && (
                    <div className="modal-action px-4 pb-4">
                        <Button
                            color={okColor}
                            loading={isOk}
                            onClick={() => {
                                setIsOk(true);
                                void handleOnModalOk();
                            }}
                        >
                            {okLabel ?? "Save"}
                        </Button>
                        <Button
                            color={cancelColor}
                            disabled={isOk}
                            onClick={handlerOnModalClose}
                        >
                            {cancelLabel ?? "Cancel"}
                        </Button>
                    </div>
                )}
            </div>
        </ModalContainer>
    );
};

const ModalContainer = styled.dialog``;

interface ModalProps {
    children?: React.ReactNode;
    className?: string;
    title?: React.ReactNode;
    size?: ComponentSize;
    okLabel?: string;
    cancelLabel?: string;
    okColor?: ComponentColor;
    cancelColor?: ComponentColor;
    showAction?: boolean;
    onClose?: () => void;
    onOk?: () => void;
}

export default Modal;
